const DEFAULT_BAUD_RATE: number = 115200;
const DEFAULT_DATA_BITS: number = 8;
const DEFAULT_STOP_BITS: number = 1;
const DEFAULT_PARITY: ParityType = 'none';
const prefixes = ['CODE', 'RFID'];

export type DeviceType = 'DEISTER_BADGE_READER' | 'FOODLES_SCANNER';

export interface DeviceInputData {
  codeType: 'qrcode' | 'rfid';
  deviceType: DeviceType;
  code: string;
}

type ListenerFunction = (inputData: DeviceInputData) => void;

export const listeners: ListenerFunction[] = [];

export function onReadCode(inputData: DeviceInputData) {
  listeners.forEach(listener => {
    listener(inputData);
  });
}

export function addListener(listener: ListenerFunction) {
  listeners.push(listener);
}

export function removeListener(listener: ListenerFunction) {
  const i = listeners.findIndex(l => l === listener);
  if (i > -1) {
    listeners.splice(i, 1);
  }
}

function accumulatorMatchPrefix(accumulator: string) {
  return prefixes.some(prefix => {
    const compareLength = Math.min(accumulator.length, prefix.length);
    return accumulator.slice(0, compareLength) === prefix.slice(0, compareLength);
  });
}

export const openSerialPort = async (
  baudRate = DEFAULT_BAUD_RATE,
  dataBits = DEFAULT_DATA_BITS,
  stopBits = DEFAULT_STOP_BITS,
  parity = DEFAULT_PARITY,
) => {
  if (!('serial' in navigator)) {
    throw new Error('Serial undefined');
  }

  const port: SerialPort = await navigator.serial.requestPort({ filters: [] });
  await port.open({
    baudRate,
    dataBits,
    stopBits,
    parity,
  });
  return port;
};

export const readFoodlesScanner = async (
  reader: ReadableStreamDefaultReader<Uint8Array>,
  onCodeRead: (inputData: DeviceInputData) => void,
) => {
  const decoder = new TextDecoder();
  const suffix = '\r';
  let acc = '';
  let code = '';
  // eslint-disable-next-line no-constant-condition
  while (true) {
    const { value } = await reader.read(); // eslint-disable-line no-await-in-loop
    const decoded = decoder.decode(value);
    acc += decoded;
    if (!accumulatorMatchPrefix(acc)) {
      console.warn(`Code not recognized (${acc}).`); // eslint-disable-line no-console
      acc = '';
    } else if (acc.endsWith(suffix)) {
      code = acc.slice(0, -suffix.length);
      console.log(`Read code ${code}`); // eslint-disable-line no-console
      const codeType = code.slice(0, 4) === 'CODE' ? 'qrcode' : 'rfid';

      if (codeType === 'qrcode') {
        code = code.slice(4);
      } else {
        code = code.slice(4).toUpperCase();
      }
      onCodeRead({ codeType, deviceType: 'FOODLES_SCANNER', code });
      acc = '';
    }
  }
};

export const readDeisterBadgeReader = async (
  reader: ReadableStreamDefaultReader<Uint8Array>,
  onCodeRead: (inputData: DeviceInputData) => void,
) => {
  const BUFFER_SIZE = 1024;
  const END_PATTERN = 0x03;
  let buffer: number[] = [];
  const decoder = new TextDecoder();

  // eslint-disable-next-line no-constant-condition
  while (true) {
    const { value } = await reader.read(); // eslint-disable-line no-await-in-loop

    if (value) {
      // eslint-disable-next-line no-plusplus
      for (let i = 0; i < value.length; i++) {
        if (buffer.length >= BUFFER_SIZE) {
          console.warn('Buffer oversized, clearing buffer'); // eslint-disable-line no-console
          buffer = [];
        }
        buffer.push(value[i]);

        // Check if we received the complete message (ends with 0x03)
        if (value[i] === END_PATTERN) {
          const uint8Array = new Uint8Array(buffer);
          const decoded = decoder.decode(uint8Array);

          const endIndex = decoded.length === 15 ? decoded.length - 3 : decoded.length - 2;
          const code = decoded.substring(5, endIndex);
          console.log(`Read deister code ${code}`); // eslint-disable-line no-console

          onCodeRead({ codeType: 'rfid', deviceType: 'DEISTER_BADGE_READER', code });
          buffer = [];
        }
      }
    }
  }
};

export const listenSerialPort = async (
  port: SerialPort,
  deviceType: string,
  onError: (err: string | Error) => void,
) => {
  if (!port) {
    return;
  }
  let lost = false;
  while (port.readable && !lost) {
    const reader = port.readable.getReader();
    try {
      if (deviceType === 'FOODLES_SCANNER') {
        await readFoodlesScanner(reader, onReadCode); // eslint-disable-line no-await-in-loop
      }
      if (deviceType === 'DEISTER_BADGE_READER') {
        await readDeisterBadgeReader(reader, onReadCode); // eslint-disable-line no-await-in-loop
      }
    } catch (error) {
      if (error.message === 'The device has been lost.') {
        lost = true;
      }
      onError(error);
    }

    reader.releaseLock();
  }
  await port.close();
  onError('Closed');
};
